import { useState } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { notificationManager } from '../../lib/notification-manager';
import { notificationScheduler } from '../../lib/notification-scheduler';
import { Send, Clock, Users, MessageCircle } from 'lucide-react';

export default function SMSTestComponent() {
  const { currentUser, family } = useFamilyContext();
  const [testing, setTesting] = useState(false);
  const [results, setResults] = useState([]);

  const addResult = (test, result) => {
    setResults(prev => [...prev, { test, result, timestamp: new Date() }]);
  };

  const testFamilyTimeInvite = async () => {
    setTesting(true);
    try {
      // Find a family member to send to (first kid if parent, or parent if kid)
      const recipient = currentUser?.isParent 
        ? family.kids[0] 
        : { id: 'parent', name: family.parentName };

      if (!recipient) {
        addResult('1:1 Invite', 'No recipient found');
        return;
      }

      const result = await notificationManager.sendNotification(
        recipient.id,
        'familyTimeInvite',
        {
          inviterName: currentUser?.name || 'Test User',
          activity: 'lunch',
          time: 'Tuesday 1pm',
          isTest: true
        }
      );

      addResult('1:1 Invite', `Sent: ${result.sent}, ${result.reason || 'Success'}`);
    } catch (error) {
      addResult('1:1 Invite', `Error: ${error.message}`);
    } finally {
      setTesting(false);
    }
  };

  const testFamilyActivityVoting = async () => {
    setTesting(true);
    try {
      const recipient = currentUser?.isParent 
        ? family.kids[0] 
        : { id: 'parent', name: family.parentName };

      if (!recipient) {
        addResult('Activity Voting', 'No recipient found');
        return;
      }

      const result = await notificationManager.sendNotification(
        recipient.id,
        'familyActivityVoting',
        {
          activityTitle: 'Beach Day Saturday',
          isTest: true
        }
      );

      addResult('Activity Voting', `Sent: ${result.sent}, ${result.reason || 'Success'}`);
    } catch (error) {
      addResult('Activity Voting', `Error: ${error.message}`);
    } finally {
      setTesting(false);
    }
  };

  const testConfirmedPlan = async () => {
    setTesting(true);
    try {
      const recipient = currentUser?.isParent 
        ? family.kids[0] 
        : { id: 'parent', name: family.parentName };

      if (!recipient) {
        addResult('Confirmed Plan', 'No recipient found');
        return;
      }

      const result = await notificationManager.sendNotification(
        recipient.id,
        'confirmedFamilyPlan',
        {
          activity: 'Movie Night',
          datetime: 'Friday 7pm',
          isTest: true
        }
      );

      addResult('Confirmed Plan', `Sent: ${result.sent}, ${result.reason || 'Success'}`);
    } catch (error) {
      addResult('Confirmed Plan', `Error: ${error.message}`);
    } finally {
      setTesting(false);
    }
  };

  const testNewIdea = async () => {
    setTesting(true);
    try {
      const recipient = currentUser?.isParent 
        ? family.kids[0] 
        : { id: 'parent', name: family.parentName };

      if (!recipient) {
        addResult('New Idea', 'No recipient found');
        return;
      }

      const result = await notificationManager.sendNotification(
        recipient.id,
        'newIdea',
        {
          title: 'Learn Photography',
          category: 'hobby',
          isTest: true
        }
      );

      addResult('New Idea', `Sent: ${result.sent}, ${result.reason || 'Success'}`);
    } catch (error) {
      addResult('New Idea', `Error: ${error.message}`);
    } finally {
      setTesting(false);
    }
  };

  const testPersonalReminders = async () => {
    setTesting(true);
    try {
      await notificationScheduler.triggerPersonalReminders();
      addResult('Personal Reminders', 'Scheduler triggered successfully');
    } catch (error) {
      addResult('Personal Reminders', `Error: ${error.message}`);
    } finally {
      setTesting(false);
    }
  };

  const testDigests = async () => {
    setTesting(true);
    try {
      await notificationScheduler.triggerDigests();
      addResult('Daily Digests', 'Scheduler triggered successfully');
    } catch (error) {
      addResult('Daily Digests', `Error: ${error.message}`);
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-neutral-50 rounded-xl shadow-sm border border-stone-200">
        <div className="p-6 border-b border-stone-200">
          <h2 className="text-2xl font-semibold text-stone-900">SMS Testing Dashboard</h2>
          <p className="mt-2 text-stone-600">Test various SMS notification types</p>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {/* Individual Notification Tests */}
            <div className="space-y-3">
              <h3 className="text-lg font-medium text-stone-900 flex items-center gap-2">
                <Send className="w-5 h-5" />
                Individual Notifications
              </h3>
              
              <button
                onClick={testFamilyTimeInvite}
                disabled={testing}
                className="w-full p-3 bg-teal-50 border border-azure-200 rounded-lg hover:bg-action-subtle transition-colors disabled:opacity-50 text-left"
              >
                <div className="font-medium text-teal-900">1:1 Invite</div>
                <div className="text-sm text-teal-700">Test invite notification</div>
              </button>

              <button
                onClick={testFamilyActivityVoting}
                disabled={testing}
                className="w-full p-3 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors disabled:opacity-50 text-left"
              >
                <div className="font-medium text-emerald-900">Activity Voting</div>
                <div className="text-sm text-emerald-700">Test voting notification</div>
              </button>

              <button
                onClick={testConfirmedPlan}
                disabled={testing}
                className="w-full p-3 bg-violet-50 border border-violet-200 rounded-lg hover:bg-violet-100 transition-colors disabled:opacity-50 text-left"
              >
                <div className="font-medium text-violet-900">Confirmed Plan</div>
                <div className="text-sm text-violet-700">Test confirmation notification</div>
              </button>

              <button
                onClick={testNewIdea}
                disabled={testing}
                className="w-full p-3 bg-yellow-50 border border-yellow-200 rounded-lg hover:bg-yellow-100 transition-colors disabled:opacity-50 text-left"
              >
                <div className="font-medium text-yellow-900">New Idea</div>
                <div className="text-sm text-yellow-700">Test idea notification</div>
              </button>
            </div>

            {/* Scheduler Tests */}
            <div className="space-y-3">
              <h3 className="text-lg font-medium text-stone-900 flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Scheduler Tests
              </h3>
              
              <button
                onClick={testPersonalReminders}
                disabled={testing}
                className="w-full p-3 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors disabled:opacity-50 text-left"
              >
                <div className="font-medium text-indigo-900">Personal Reminders</div>
                <div className="text-sm text-indigo-700">Trigger reminder check</div>
              </button>

              <button
                onClick={testDigests}
                disabled={testing}
                className="w-full p-3 bg-pink-50 border border-pink-200 rounded-lg hover:bg-pink-100 transition-colors disabled:opacity-50 text-left"
              >
                <div className="font-medium text-pink-900">Daily Digests</div>
                <div className="text-sm text-pink-700">Trigger digest check</div>
              </button>
            </div>
          </div>

          {/* Results */}
          {results.length > 0 && (
            <div className="mt-6">
              <h3 className="text-lg font-medium text-stone-900 mb-4 flex items-center gap-2">
                <MessageCircle className="w-5 h-5" />
                Test Results
              </h3>
              
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {results.slice().reverse().map((result, index) => (
                  <div key={index} className="p-3 bg-stone-50 rounded-lg border">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium text-stone-900">{result.test}</div>
                        <div className="text-sm text-stone-600">{result.result}</div>
                      </div>
                      <div className="text-xs text-stone-500">
                        {result.timestamp.toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <button
                onClick={() => setResults([])}
                className="mt-3 px-4 py-2 text-sm text-stone-600 hover:text-stone-800 underline"
              >
                Clear Results
              </button>
            </div>
          )}

          {/* Instructions */}
          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <h4 className="font-medium text-yellow-900 mb-2">Testing Instructions</h4>
            <ul className="text-sm text-yellow-800 space-y-1">
              <li>• Make sure you have SMS preferences configured first</li>
              <li>• Check the console for detailed logs</li>
              <li>• Individual notifications respect user preferences</li>
              <li>• Scheduler tests check current conditions</li>
              <li>• Messages are logged to the sms_logs table</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}